<template>
  <div class="space-y-6">
    <!-- Action buttons -->
    <div class="flex items-center justify-end gap-3">
      <router-link to="/medias" class="btn-secondary">
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
        </svg>
        Back
      </router-link>
      <button v-if="$checkPermission('medias.update')" @click="save" :disabled="saving" class="btn-primary">
        <svg v-if="!saving" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
        </svg>
        {{ saving ? 'Saving...' : 'Save' }}
      </button>
    </div>

    <!-- Row 1: Player (60%) + Details & File Info (40%) -->
    <div class="grid grid-cols-1 lg:grid-cols-[6fr_4fr] gap-6 items-stretch">
      <!-- Player -->
      <div class="bg-black rounded-xl overflow-hidden card-shadow relative min-h-[300px]">
        <VideoPlayer
          v-if="activeFormatUrl"
          ref="player"
          :src="activeFormatUrl"
          :type="activeFormatType"
          class="absolute inset-0 w-full h-full"
          @playing="isPlaying = true"
          @paused="isPlaying = false"
        />
        <div v-else class="absolute inset-0 flex flex-col items-center justify-center text-center">
          <button v-if="firstPlayableFormat" @click="playFirstFormat" class="w-16 h-16 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-all hover:scale-110 group">
            <svg class="w-7 h-7 text-white/70 group-hover:text-white ml-1 transition-colors" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
          </button>
          <p v-if="firstPlayableFormat" class="text-xs text-gray-500 mt-3">{{ firstPlayableFormat.id }}</p>
          <template v-else>
            <svg class="w-12 h-12 text-gray-600 mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
            </svg>
            <p class="text-sm text-gray-500">No formats available</p>
          </template>
        </div>
      </div>

      <!-- Right column -->
      <div class="space-y-6">
        <!-- Details -->
        <div class="bg-white rounded-xl border border-gray-200/60 card-shadow">
          <div class="px-6 py-3 border-b border-gray-100">
            <h3 class="text-[15px] font-semibold text-gray-900">Details</h3>
          </div>
          <div class="p-6 space-y-5">
            <div class="grid gap-3" style="grid-template-columns: 70% 30%">
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">ID</label>
                <div class="flex items-center w-full h-[42px] px-3.5 rounded-lg border border-gray-200 bg-gray-50">
                  <span class="text-sm font-data text-gray-500 truncate flex-1">{{ mediaId }}</span>
                  <button @click="copyToClipboard(mediaId, 'ID')" class="p-1 rounded text-gray-400 hover:text-[var(--color-primary)] transition-colors shrink-0 ml-2" title="Copy ID">
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Status</label>
                <select v-model="form.status">
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Name <span class="text-xs text-gray-400 font-normal">(max 100)</span></label>
              <input v-model="form.title" type="text" maxlength="100" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Description</label>
              <textarea v-model="form.description" rows="3" placeholder="Add a description for this media" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Tags</label>
              <TagSelect v-model="form.tags" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Category</label>
              <CategorySelect v-model="form.categories" />
            </div>
          </div>
        </div>

        <!-- File info -->
        <div class="bg-white rounded-xl border border-gray-200/60 card-shadow">
          <div class="px-6 py-3 border-b border-gray-100">
            <h3 class="text-[15px] font-semibold text-gray-900">File Info</h3>
          </div>
          <div class="p-6 space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-400">Size</span>
              <span class="text-sm font-data text-gray-700">{{ $filters.formatBytes(media.file_object?.size || 0) }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-400">Status</span>
              <span :class="fileStatusClass">{{ media.file_object?.status || '—' }}</span>
            </div>
            <div v-if="media.file_object?.content_type" class="flex items-center justify-between">
              <span class="text-sm text-gray-400">Type</span>
              <span class="text-sm font-data text-gray-700">{{ media.file_object.content_type }}</span>
            </div>
            <div v-if="media.created_at" class="flex items-center justify-between">
              <span class="text-sm text-gray-400">Created</span>
              <span class="text-sm font-data text-gray-700">{{ $filters.formatDatetime(media.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Row 2: Formats — full width -->
    <div class="bg-white rounded-xl border border-gray-200/60 card-shadow">
      <div class="px-6 py-3 border-b border-gray-100">
        <h3 class="text-[15px] font-semibold text-gray-900">Formats</h3>
        <p class="text-sm text-gray-400 mt-0.5">Available transcoded versions</p>
      </div>
      <div v-if="formatList.length" class="divide-y divide-gray-100">
        <div v-for="fmt in formatList" :key="fmt.id" :class="['flex items-center justify-between px-6 py-4 transition-colors', fmt.status === 'failed' ? 'bg-red-50/50' : activeFormat === fmt.id ? 'bg-[var(--color-primary-light)]' : 'hover:bg-gray-50/50']">
          <div class="flex items-center gap-3 min-w-0">
            <div :class="['w-9 h-9 rounded-lg flex items-center justify-center shrink-0', fmt.status === 'failed' ? 'bg-red-100' : activeFormat === fmt.id ? 'bg-[var(--color-primary-glow)]' : formatIconBg(fmt.id)]">
              <!-- Failed icon -->
              <svg v-if="fmt.status === 'failed'" class="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              <!-- HLS/ABR: signal icon -->
              <svg v-else-if="fmt.id === 'original_abr'" class="w-4 h-4" :class="activeFormat === fmt.id ? 'text-[var(--color-primary)]' : 'text-green-500'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M2 16.1A5 5 0 0 1 5.9 20M2 12.05A9 9 0 0 1 9.95 20M2 8V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-6"/><line x1="2" y1="20" x2="2.01" y2="20"/></svg>
              <!-- MP3: music icon -->
              <svg v-else-if="fmt.id === 'mp3'" class="w-4 h-4" :class="activeFormat === fmt.id ? 'text-[var(--color-primary)]' : 'text-pink-500'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
              <!-- MP4/video: film icon -->
              <svg v-else class="w-4 h-4" :class="activeFormat === fmt.id ? 'text-[var(--color-primary)]' : 'text-violet-500'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
            </div>
            <div class="min-w-0">
              <p class="text-sm font-medium" :class="fmt.status === 'failed' ? 'text-red-600' : activeFormat === fmt.id ? 'text-[var(--color-primary)]' : 'text-gray-900'">{{ fmt.id }}</p>
              <p v-if="fmt.status === 'failed'" class="text-sm text-red-400">Transcoding failed</p>
              <p v-else class="text-xs font-data text-gray-400 truncate">{{ $filters.formatBytes(fmt.fileSize || 0) }}</p>
            </div>
          </div>
          <!-- Actions: only when success -->
          <div v-if="fmt.status === 'success'" class="flex items-center gap-1.5 shrink-0">
            <button @click="playFormat(fmt)" :class="['p-2 rounded-lg transition-colors', activeFormat === fmt.id ? 'text-[var(--color-primary)] bg-[var(--color-primary-glow)]' : 'text-gray-400 hover:text-green-500 hover:bg-green-50']" :title="activeFormat === fmt.id && isPlaying ? 'Pause' : 'Play'">
              <svg v-if="activeFormat === fmt.id && isPlaying" class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
              <svg v-else class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            </button>
            <a v-if="fmt.remoteUrl && fmt.id !== 'original_abr'" :href="$mediaUrl.media(mediaId, fmt.id)" target="_blank" download class="p-2 rounded-lg text-gray-400 hover:text-amber-500 hover:bg-amber-50 transition-colors" title="Download">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </a>
            <button v-if="fmt.remoteUrl" @click="copyToClipboard($mediaUrl.media(mediaId, fmt.id))" class="p-2 rounded-lg text-gray-400 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] transition-colors" title="Copy URL">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
            </button>
            <YouTubePublishButton
              v-if="fmt.remoteUrl && fmt.id !== 'original_abr' && fmt.id !== 'mp3'"
              :media-id="mediaId"
              :media-title="form.title || ''"
              :media-description="form.description || ''"
              :formats="[fmt]"
              :youtube-publishes="media.youtubePublishes || {}"
              :preselect-format="fmt.id"
              @updated="media.youtubePublishes = $event"
            />
          </div>
          <!-- Failed badge -->
          <span v-else-if="fmt.status === 'failed'" class="badge-error shrink-0">Failed</span>
          <!-- Processing -->
          <span v-else-if="fmt.status !== 'success'" class="badge-warning shrink-0">{{ fmt.status }}</span>
        </div>
      </div>
      <!-- HLS -->
      <div v-if="media.url?.hls && !hasOriginalAbr" class="px-6 py-4 border-t border-gray-100">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3 min-w-0">
            <div :class="['w-9 h-9 rounded-lg flex items-center justify-center shrink-0', activeFormat === 'hls' ? 'bg-[var(--color-primary-glow)]' : 'bg-green-50']">
              <svg class="w-4 h-4" :class="activeFormat === 'hls' ? 'text-[var(--color-primary)]' : 'text-green-500'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="4"/><path d="M16.24 7.76a6 6 0 010 8.49m-8.48-8.49a6 6 0 000 8.49" stroke-linecap="round"/></svg>
            </div>
            <div class="min-w-0">
              <p class="text-sm font-medium" :class="activeFormat === 'hls' ? 'text-[var(--color-primary)]' : 'text-gray-900'">HLS Stream</p>
              <p class="text-xs font-data text-gray-400 truncate">{{ media.url.hls }}</p>
            </div>
          </div>
          <div class="flex items-center gap-1.5 shrink-0">
            <button @click="playHls" :class="['p-2 rounded-lg transition-colors', activeFormat === 'hls' ? 'text-[var(--color-primary)] bg-[var(--color-primary-glow)]' : 'text-gray-400 hover:text-green-500 hover:bg-green-50']">
              <svg v-if="activeFormat === 'hls' && isPlaying" class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
              <svg v-else class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            </button>
            <button @click="copyToClipboard($mediaUrl.media(mediaId, 'hls'))" class="p-2 rounded-lg text-gray-400 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] transition-colors">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
            </button>
          </div>
        </div>
      </div>
      <div v-if="!formatList.length && !(media.url?.hls && !hasOriginalAbr)" class="px-6 py-12 text-center">
        <div class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
          <svg class="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
        </div>
        <p class="text-sm text-gray-500">{{ media.file_object?.status === 'transcoding' ? 'Transcoding in progress...' : 'No formats available' }}</p>
      </div>
    </div>

    <!-- Row 3: Thumbnails -->
    <div class="bg-white rounded-xl border border-gray-200/60 card-shadow">
      <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 class="text-[15px] font-semibold text-gray-900">Thumbnails</h3>
          <p class="text-sm text-gray-400 mt-0.5">{{ media.thumbs?.length ? 'Select a default thumbnail for this media' : 'Generate thumbnails from your video' }}</p>
        </div>
        <button @click="generateThumbnails" :disabled="generatingThumbs" class="btn-primary !text-xs !px-3 !py-1.5">
          <svg v-if="!generatingThumbs" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
          {{ generatingThumbs ? 'Generating...' : 'Generate Thumbnails' }}
        </button>
      </div>
      <div class="p-6">
        <div v-if="media.thumbs && media.thumbs.length" class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
          <div
            v-for="(thumb, i) in media.thumbs" :key="i"
            :class="['relative rounded-xl overflow-hidden transition-all group', media.thumb_url === thumb.path ? 'ring-2 ring-[var(--color-primary)] ring-offset-2' : 'ring-1 ring-gray-200 hover:ring-gray-300']"
          >
            <img :src="$mediaUrl.thumbUrl(thumb.path)" :alt="'Thumbnail ' + (i + 1)" :class="['w-full object-cover bg-gray-100', isVerticalThumb(thumb) ? 'aspect-[9/16]' : 'aspect-video']" />
            <!-- Info bar -->
            <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent pt-4 pb-1.5 px-2">
              <div class="flex items-center justify-between">
                <span v-if="thumb.width && thumb.height" class="text-[10px] font-data text-white/80">{{ thumb.width }}x{{ thumb.height }}</span>
                <span v-if="thumb.filesize" class="text-[10px] font-data text-white/80">{{ $filters.formatBytes(thumb.filesize) }}</span>
              </div>
            </div>
            <!-- Default badge -->
            <div v-if="media.thumb_url === thumb.path" class="absolute top-2 left-2">
              <span class="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[var(--color-primary)] text-white text-[10px] font-bold shadow-lg shadow-[var(--color-primary-glow)]">
                <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                Default
              </span>
            </div>
            <!-- Hover overlay -->
            <div class="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200" />
            <!-- Action buttons (top-right) -->
            <div class="absolute top-2 right-2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-200 translate-y-1 group-hover:translate-y-0">
              <button v-if="media.thumb_url !== thumb.path" @click.stop="setDefaultThumb(thumb)" class="w-7 h-7 rounded-lg bg-white/90 hover:bg-white text-gray-700 hover:text-[var(--color-primary)] flex items-center justify-center shadow-lg backdrop-blur-sm transition-colors" title="Set as default">
                <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              </button>
              <a @click.stop :href="$mediaUrl.thumbUrl(thumb.path)" download target="_blank" class="w-7 h-7 rounded-lg bg-white/90 hover:bg-white text-gray-700 hover:text-blue-500 flex items-center justify-center shadow-lg backdrop-blur-sm transition-colors" title="Download">
                <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              </a>
              <button v-if="media.thumb_url !== thumb.path" @click.stop="confirmDeleteThumb(thumb)" class="w-7 h-7 rounded-lg bg-white/90 hover:bg-white text-gray-700 hover:text-red-500 flex items-center justify-center shadow-lg backdrop-blur-sm transition-colors" title="Delete">
                <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
              </button>
            </div>
          </div>
          <!-- Upload thumbnail button -->
          <div
            @click="$refs.thumbUpload.click()"
            :class="['relative rounded-lg overflow-hidden cursor-pointer transition-all border-2 border-dashed border-gray-300 hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-light)] flex flex-col items-center justify-center', isVerticalThumb(media.thumbs[0]) ? 'aspect-[9/16]' : 'aspect-video']"
          >
            <svg v-if="!uploadingThumb" class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <div v-else class="cs-spinner" />
            <span class="text-[10px] font-medium mt-1.5" :class="uploadingThumb ? 'text-[var(--color-primary)]' : 'text-gray-400'">{{ uploadingThumb ? 'Uploading...' : 'Upload' }}</span>
          </div>
          <input ref="thumbUpload" type="file" accept="image/*" class="hidden" @change="onThumbFileSelected" />
        </div>
        <div v-else class="text-center py-8">
          <div class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
            <svg class="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
          </div>
          <p class="text-sm text-gray-500">No thumbnails available</p>
          <p class="text-sm text-gray-400 mt-0.5">Generate thumbnails or upload your own</p>
          <button @click="$refs.thumbUploadEmpty.click()" :disabled="uploadingThumb" class="btn-secondary !text-xs mt-3">
            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            {{ uploadingThumb ? 'Uploading...' : 'Upload Thumbnail' }}
          </button>
          <input ref="thumbUploadEmpty" type="file" accept="image/*" class="hidden" @change="onThumbFileSelected" />
        </div>
      </div>
    </div>

    <!-- Row 4: Delete -->
    <DeleteResource
      v-if="$checkPermission('medias.delete')"
      warning-title="You are deleting this media"
      warning-text="This will permanently remove the file and all transcoded formats."
      button-label="Delete Media"
      confirm-label="I confirm this media deletion"
      :confirm-message="`Delete '${media.title}'? This cannot be undone.`"
      success-message="Media deleted successfully"
      :disabled-message="!canDelete ? 'Cannot delete: media is published or transcoding.' : ''"
      :endpoint="`/medias/${mediaId}`"
      @deleted="$router.push('/medias')"
    />

    <ConfirmModal
      :show="showGenerateConfirm"
      title="Generate Thumbnails"
      message="This will generate new thumbnails from your video. Any existing thumbnails will be replaced."
      confirm-text="Generate"
      @confirm="doGenerateThumbnails"
      @cancel="showGenerateConfirm = false"
    />

    <ConfirmModal
      :show="showSetDefaultConfirm"
      title="Set Default Thumbnail"
      message="Set this as the default thumbnail? You will need to save the media for the change to take effect."
      confirm-text="Set Default"
      @confirm="doSetDefaultThumb"
      @cancel="showSetDefaultConfirm = false"
    />

    <ConfirmModal
      :show="showDeleteThumbConfirm"
      :loading="deletingThumb"
      title="Delete Thumbnail"
      message="This will permanently remove this thumbnail. This action cannot be undone."
      confirm-text="Delete"
      :danger="true"
      @confirm="doDeleteThumb"
      @cancel="showDeleteThumbConfirm = false"
    />
  </div>
</template>

<script>
import DeleteResource from '../../components/DeleteResource.vue'
import ConfirmModal from '../../components/ConfirmModal.vue'
import TagSelect from '../../components/TagSelect.vue'
import CategorySelect from '../../components/CategorySelect.vue'
import VideoPlayer from '../../components/VideoPlayer.vue'
import YouTubePublishButton from '../../components/YouTubePublishButton.vue'
import api from '../../services/api.js'
import { notifySuccess, notifyError } from '../../services/notifications.js'
import { notifyWarning } from "@/services/notifications";

export default {
  name: 'MediaEdit',
  components: { DeleteResource, ConfirmModal, TagSelect, CategorySelect, VideoPlayer, YouTubePublishButton },

  data() {
    return {
      mediaId: null,
      media: {},
      form: { title: '', description: '', status: 'draft', tags: [], categories: [] },
      saving: false,
      activeFormat: null,
      isPlaying: false,
      generatingThumbs: false,
      uploadingThumb: false,
      showDeleteThumbConfirm: false,
      deletingThumb: false,
      deleteThumbTarget: null,
      showGenerateConfirm: false,
      showSetDefaultConfirm: false,
      pendingThumb: null
    }
  },

  computed: {
    fileStatusClass() {
      const s = this.media.file_object?.status
      if (s === 'success') return 'badge-live'
      if (s === 'failed') return 'badge-error'
      if (['transcoding', 'uploading', 'processing'].includes(s)) return 'badge-warning'
      return 'badge-idle'
    },
    hasOriginalAbr() {
      return this.formatList.some(f => f.id === 'original_abr')
    },
    canDelete() {
      return this.media.status !== 'published' && this.media.file_object?.status !== 'transcoding'
    },
    formatList() {
      if (!this.media.exported_files) return []
      return Object.entries(this.media.exported_files).map(([id, file]) => ({
        id,
        remoteUrl: file.remoteUrl || file.remotePath,
        fileSize: file.fileSize,
        status: file.status || (file.remoteUrl || file.remotePath ? 'success' : 'pending')
      }))
    },
    firstPlayableFormat() {
      const fmt = this.formatList.find(f => f.status !== 'failed' && f.remoteUrl)
      if (fmt) return fmt
      if (this.media.url?.hls || this.hasOriginalAbr) return { id: 'hls', remoteUrl: true }
      return null
    },
    activeFormatUrl() {
      if (this.activeFormat === 'hls') return this.$mediaUrl.media(this.mediaId, 'hls')
      if (this.activeFormat) return this.$mediaUrl.media(this.mediaId, this.activeFormat)
      return ''
    },
    activeFormatType() {
      if (this.activeFormat === 'hls' || this.activeFormat === 'original_abr') return 'application/x-mpegURL'
      if (this.activeFormat === 'mp3') return 'audio/mpeg'
      return 'video/mp4'
    },
  },

  async mounted() {
    this.mediaId = this.$route.params.id
    await this.loadMedia()
  },

  methods: {
    isVerticalThumb(thumb) {
      return thumb.width && thumb.height && thumb.height > thumb.width
    },
    async loadMedia() {
      try {
        const res = await api.get(`/medias/${this.mediaId}`)
        this.media = res.data?.media || res.data || {}
        this.form.title = this.media.title || this.media.name || ''
        this.form.description = this.media.description || ''
        this.form.status = this.media.status || 'draft'
        this.form.tags = (this.media.tags || []).map(t => typeof t === 'string' ? t : t._id)
        this.form.categories = (this.media.categories || []).map(c => typeof c === 'string' ? c : c._id?.toString())
      } catch (err) {
        notifyError(err?.message || 'Failed to load media')
      }
    },

    generateThumbnails() {
      this.showGenerateConfirm = true
    },

    async doGenerateThumbnails() {
      this.showGenerateConfirm = false
      this.generatingThumbs = true
      try {
        await api.put(`/medias/${this.mediaId}/thumb`)
        notifySuccess('Thumbnail generation queued. This may take a few moments.')
      } catch (err) {
        notifyError(err?.message || 'Failed to generate thumbnails')
      } finally { this.generatingThumbs = false }
    },

    async onThumbFileSelected(e) {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return

      if (!file.type.startsWith('image/')) {
        notifyError('Only image files are allowed')
        return
      }

      // Validate dimensions
      const dims = await this.getImageDimensions(file)
      const mediaOrientation = this.media.file_object?.orientation || 'horizontal'
      const isPortrait = dims.height > dims.width
      if (mediaOrientation === 'vertical' && !isPortrait) {
        notifyError('This media requires a portrait (vertical) thumbnail')
        return
      }
      if (mediaOrientation === 'horizontal' && isPortrait) {
        notifyError('This media requires a landscape (horizontal) thumbnail')
        return
      }

      this.uploadingThumb = true
      try {
        // Get upload session from API
        const res = await api.post(`/medias/${this.mediaId}/thumb/upload`, {
          filename: file.name,
          contentType: file.type,
          width: dims.width,
          height: dims.height,
          filesize: file.size
        })

        const uploadUrl = res.data?.uploadUrl
        if (!uploadUrl) throw new Error('No upload URL returned')

        // Upload directly to GCP
        await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file
        })

        // Refresh media to get updated thumbs
        await this.loadMedia()
        notifySuccess('Thumbnail uploaded')
      } catch (err) {
        notifyError(err?.message || 'Failed to upload thumbnail')
      } finally {
        this.uploadingThumb = false
      }
    },

    getImageDimensions(file) {
      return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          resolve({ width: img.naturalWidth, height: img.naturalHeight })
          URL.revokeObjectURL(img.src)
        }
        img.onerror = () => resolve({ width: 0, height: 0 })
        img.src = URL.createObjectURL(file)
      })
    },

    confirmDeleteThumb(thumb) {
      this.deleteThumbTarget = thumb
      this.showDeleteThumbConfirm = true
    },

    async doDeleteThumb() {
      if (!this.deleteThumbTarget) return
      this.deletingThumb = true
      try {
        await api.delete(`/medias/${this.mediaId}/thumb`, {
          data: { path: this.deleteThumbTarget.path }
        })
        await this.loadMedia()
        notifySuccess('Thumbnail deleted')
      } catch (err) {
        notifyError(err?.message || 'Failed to delete thumbnail')
      } finally {
        this.deletingThumb = false
        this.showDeleteThumbConfirm = false
        this.deleteThumbTarget = null
      }
    },

    setDefaultThumb(thumb) {
      if (this.media.thumb_url === thumb.path) return
      this.pendingThumb = thumb
      this.showSetDefaultConfirm = true
    },

    doSetDefaultThumb() {
      this.showSetDefaultConfirm = false
      if (!this.pendingThumb) return
      try {
        this.media.thumb_url = this.pendingThumb.path
        notifyWarning('Default thumbnail updated. The media need to be saved')
      } catch (err) {
        notifyError(err?.message || 'Failed to update thumbnail')
      }
    },

    async save() {
      this.saving = true
      try {
        await api.put(`/medias/${this.mediaId}`, {
          media: {
            title: this.form.title,
            description: this.form.description,
            status: this.form.status,
            tags: this.form.tags,
            categories: this.form.categories,
            thumb_url: this.media.thumb_url || null
          }
        })
        notifySuccess('Media updated')
      } catch (err) {
        notifyError(err?.message || 'Failed to update media')
      } finally {
        this.saving = false
      }
    },

    playFirstFormat() {
      const fmt = this.firstPlayableFormat
      if (!fmt) return
      if (fmt.id === 'hls') this.activeFormat = 'hls'
      else this.activeFormat = fmt.id
    },

    playFormat(fmt) {
      if (this.activeFormat === fmt.id) {
        this.$refs.player?.togglePlay()
      } else {
        this.activeFormat = fmt.id
      }
    },

    playHls() {
      if (this.activeFormat === 'hls') {
        this.$refs.player?.togglePlay()
      } else {
        this.activeFormat = 'hls'
      }
    },

    formatIconBg(id) {
      if (id === 'original_abr') return 'bg-green-50'
      if (id === 'mp3') return 'bg-pink-50'
      return 'bg-violet-50'
    },

    async copyToClipboard(text, label = 'URL') {
      await navigator.clipboard.writeText(text)
      notifySuccess(`${label} copied!`)
    },

  }
}
</script>
